'use client';

import { map as _map, get as _get } from 'lodash-es';
import { Fragment } from 'react';
import { useParams } from 'next/navigation';
import { useSuspenseQuery } from '@apollo/client/react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import ErrorMessage from '@/components/error-message';
import NotFound from '@/components/not-found';
import Markdown from '@/components/markdown';
import { cityQuery } from '@/apollo/cities';
import Main from '@/components/main';
import MaterialLink from '@/components/material-link';
import { useAppContext } from '@/contexts/app';
import MaterialLinkButton from '@/components/material-link-button';
import config from '@/config';
import PrimaryToggle from '@/components/cities/primary-toggle';

export default function CityPageClient() {
  const params = useParams();
  const { city: appCity } = useAppContext();

  const { data: cityData, error } = useSuspenseQuery(cityQuery, {
    variables: {
      slug: params.citySlug,
    },
  });
  const city = _get(cityData, 'city');
  const territories = _get(city, 'territoriesJson') ? JSON.parse(_get(city, 'territoriesJson')) : null;

  if (error) {
    return <ErrorMessage message='An error occurred while loading city.' error={error} />;
  }
  if (!city) { return <NotFound />; }

  return (
    <Main compact>
      <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', '& > *:first-of-type': { flexGrow: 1 } }}>
        <div>
          <Typography variant="h1" sx={{ m: 0 }}>{city.name}</Typography>
          <Box component="div" sx={{ display: 'block' }}>{city.locality}</Box>
        </div>
        <div>
          {_get(appCity, 'slug') === params.citySlug ? (
            <MaterialLinkButton
              href="/calendar"
              variant="contained"
              color="primary"
            >
              View Calendar
            </MaterialLinkButton>
          ) : (
            <PrimaryToggle city={city} />
          )}
        </div>
      </Box>

      <Markdown>{city.description}</Markdown>

      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h2">Land Acknowledgement</Typography>
        <p>
          {city.name} is located on the stolen lands of the peoples of{' '}
          <Fragment>
            {_map(territories, (territory, index) => (
              <Fragment key={index}>
                {index > 0 && ', '}
                {index === territories.length - 1 && 'and '}
                <MaterialLink href={territory.properties.description}>
                  {territory.properties.Name}
                </MaterialLink>
              </Fragment>
            ))}
          </Fragment>
          {'.'}
        </p>
        <Box
          component="iframe"
          sx={{ width: '100%', height: 500, border: 'none', bgcolor: 'grey.300' }}
          src={`https://native-land.ca/api/embed/embed.html?maps=territories&position=${city.latitude},${city.longitude}&key=${config().universal.nativeLandApiKey}`}
        />
        <p>Maps and territories data provided by <MaterialLink href="https://native-land.ca/">native-land.ca</MaterialLink></p>
      </Paper>

      {city.resources && (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h2">Resources</Typography>
          <Markdown>{city.resources}</Markdown>
        </Paper>
      )}
    </Main>
  );
}
